/**
 * floor-calendar-data — the data adapter behind the Floor Calendar screen
 * (calendar insertion phase 2; decisions in docs/calendar-data-type-map.md,
 * source map in docs/floor-calendar-port.md, claim c-2026-0808-01g).
 *
 * ONE hook assembles the day board the prototype rendered from its inline
 * TENANTS seed — but every field here arrives through the serving path:
 *   staff columns + shifts ........ getOpsBoard / getScheduleWeek (wf-api)
 *   appointments (full detail) .... listAppointments (appointments-api)
 *   events ........................ ops board events (StudioEventDto-backed)
 *   queues + live entries ......... ops board queues
 *   manager calendar blocks ....... calendar-day blocks client
 *   external Outlook/Google busy .. ABSENT until REQ-CAL-2's listExternalBusy
 *                                   lands (seed doc external-busy.json is
 *                                   ready); the board renders nothing rather
 *                                   than inventing busy time.
 * No fixture data lives in this file — the no-fake-interactivity mandate and
 * the serving-path rule both apply in full.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Appointment, listAppointments, listAppointmentServices,
  rescheduleAppointment, cancelAppointment, checkInAppointment, markAppointmentNoShow,
} from "../appointments/appointments-api";
import { getOpsBoard, OpsBoardSnapshot, listEventStaffing, assignEventRole, getScheduleWeek, WfShift } from "../workforce/wf-api";
import { apiRaw, listEventGuests, listInbox } from "@v0y4ge/api-client";
import { listQueues } from "../queues/queues-api";
import { fetchEventsList } from "../../lib/api";
import { toUIEvent } from "../../lib/events-adapter";
/* calendar-day rides the barrel as a NAMESPACE export (`export * as calendarDay`)
 * — the preview resolver has no package-subpath mapping for a deep import. */
import { calendarDay } from "@v0y4ge/api-client";
const { listCalendarDayBlocks, createCalendarDayBlock, deleteCalendarDayBlock } = calendarDay;

/** Grid window, minutes-of-day — same span the prototype board proved. */
export const DAY_START = 8 * 60;
export const DAY_END = 19 * 60;

export const hmToMin = (hm: string): number => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hm || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};
export const minToHm = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

export interface FloorStaffCol {
  staffId: string;
  name: string;
  role: string;
  /** live floor status when the ops board reports one */
  status?: string | null;
}
export interface FloorApptRow {
  appt: Appointment;
  staffId: string | null;
  staffName: string | null;
  fromMin: number;
  toMin: number;
}
export interface FloorEventRow {
  id: string;
  /** the matched StudioEventDto raw row (capacity/description/category live here) */
  studio?: Record<string, unknown> | null;
  /** StudioEventDto id (sw_*) — the id Event Day actually reads. Null when the
   * ops board advertises an event the events family doesn't carry for this
   * date; the rail then renders WITHOUT the jump-off rather than deep-linking
   * into a denial. */
  studioId: string | null;
  title: string;
  fromMin: number;
  toMin: number;
  registered: number;
  checkedIn: number;
  staffOnShift: number;
  staffNeeded: number;
  status: string;
  /** The room the event occupies, when the served row names one (REQ-CAL-4). */
  resource: string | null;
}
export interface FloorQueueRow {
  id: string;
  /** The Concierge family's own queue id (its `?q=` param) — joined by NAME;
   * the ops board's synthetic id means nothing to that screen. */
  consoleId: string | null;
  name: string;
  waiting: number;
  avgWaitMins: number;
  longestWaitMins: number;
  staffed: number;
  needed: number;
  entries: { id: string; name: string; joinedAt: string; service?: string | null; status: string }[];
}
/** Queue-duty rota block on a staff column (served: /v1/admin/floor/queue-duty). */
export interface FloorDutyRow {
  id: string;
  staffName: string;
  queueName: string;
  fromMin: number;
  toMin: number;
}
/** Event-coverage assignment on a staff column (workforce event-staffing family). */
export interface FloorCoverageRow {
  id: string;
  eventId: string;
  roleId: string;
  roleLabel: string;
  eventTitle: string;
  staffId: string;
  staffName: string;
  fromMin: number;
  toMin: number;
}
export interface FloorRoomCol {
  id: string;
  name: string;
}
/** A staff member's shift window + breaks for the day (workforce schedules). */
export interface FloorShiftRow {
  staffId: string | null;
  staffName: string;
  fromMin: number;
  toMin: number;
  breaks: { fromMin: number; toMin: number; kind: string }[];
}
/** Service name → list price, for the derived revenue tile. */
export type FloorPriceMap = Record<string, number>;
export interface FloorBlockRow {
  id: string;
  label: string;
  staffId?: string | null;
  /** external-busy rows join by name (no staff ids in that family) */
  staffName?: string;
  fromMin: number;
  toMin: number;
  /** calendar-day block kind as served; the screen treats unknown kinds as busy */
  kind?: string | null;
}
export interface FloorDay {
  dateISO: string;
  tz: string | null;
  locationId: string | null;
  locationName: string | null;
  staff: FloorStaffCol[];
  appointments: FloorApptRow[];
  events: FloorEventRow[];
  queues: FloorQueueRow[];
  blocks: FloorBlockRow[];
  duty: FloorDutyRow[];
  coverage: FloorCoverageRow[];
  rooms: FloorRoomCol[];
  shifts: FloorShiftRow[];
  prices: FloorPriceMap;
  /** event guest rows in the prototype's shape: [name, tier, status] */
  guests: [string, string, string][];
  /** inbox conversations in the prototype's thread shape (msgs load lazily) */
  threads: { id: string; name: string; chan: string; when: string; unread: boolean; preview: string; context: string; msgs: unknown[] }[];
  /** REQ-CAL-2 seam: stays empty (never invented) until listExternalBusy serves. */
  externalBusy: FloorBlockRow[];
  /** which reads answered — drives the DataSourceStrip */
  sources: { op: string; ok: boolean; error?: string }[];
}

const apptRow = (a: Appointment): FloorApptRow => ({
  appt: a,
  staffId: a.staffId,
  staffName: a.staffName,
  fromMin: hmToMin(a.start),
  toMin: hmToMin(a.end),
});

/** The ops-board floor roster and the appointments family use DIFFERENT staff id
 * schemes (wf-layer ids vs seed-world rows that carry only the staff NAME with
 * staffId null) — the durable shared key is the person's name. Normalized for
 * the join; ids still win when both sides carry one. */
export const staffKey = (name: string | null | undefined): string => (name || "").trim().toLowerCase();

/** ISO time → minutes-of-day in the board's wall clock (server sends wall-clock
 *  HH:MM for ops rows; ISO datetimes fall back to their local clock face). */
const anyToMin = (v: string): number => {
  if (/^\d{1,2}:\d{2}/.test(v)) return hmToMin(v);
  const d = new Date(v);
  return isNaN(d.getTime()) ? 0 : d.getHours() * 60 + d.getMinutes();
};

export function useFloorCalendarDay(dateISO: string, locationId?: string | null) {
  const [day, setDay] = useState<FloorDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true);
    setError(null);
    const sources: FloorDay["sources"] = [];
    const grab = async <T,>(op: string, p: Promise<T>): Promise<T | null> => {
      try {
        const r = await p;
        sources.push({ op, ok: true });
        return r;
      } catch (e) {
        sources.push({ op, ok: false, error: e instanceof Error ? e.message : String(e) });
        return null;
      }
    };
    const mondayOf = (iso: string) => { const d = new Date(iso + "T12:00:00"); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return d.toISOString().slice(0, 10); };
    const [appts, board, blocks, consoleQueues, studioEvents, dutyRes, staffingRes, roomsRes, weekRes, busyRes, svcRes, inboxRes] = await Promise.all([
      grab("listAppointments", listAppointments({ date: dateISO, locationId: locationId ?? undefined })),
      grab("getOpsBoard", getOpsBoard(dateISO, locationId ?? undefined)),
      grab("listCalendarDayBlocks", listCalendarDayBlocks({ date: dateISO })),
      /* jump-off id joins: the two target screens read DIFFERENT families than
       * the ops board — the Concierge's listQueues and the events family are
       * the id spaces their deep links understand. Name/title is the join key. */
      grab("listQueues", listQueues(null)),
      grab("fetchEventsList", fetchEventsList({ from: dateISO, to: dateISO })),
      grab("listQueueDuty", apiRaw<{ duty: unknown[] }>(`/v1/admin/floor/queue-duty?date=${encodeURIComponent(dateISO)}`, undefined, "listQueueDuty")),
      grab("listEventStaffing", listEventStaffing(dateISO, dateISO, locationId ?? undefined)),
      grab("listResources", apiRaw<Record<string, unknown>>("/v1/admin/resources-editor/resources", undefined, "listResources")),
      grab("getScheduleWeek", getScheduleWeek(mondayOf(dateISO), locationId ?? undefined)),
      grab("listExternalBusy", apiRaw<{ blocks: unknown[] }>(`/v1/admin/floor/external-busy?date=${encodeURIComponent(dateISO)}`, undefined, "listExternalBusy")),
      grab("listAppointmentServices", listAppointmentServices()),
      grab("listInbox", (listInbox as unknown as (q?: Record<string, unknown>) => Promise<unknown>)({ limit: 12 })),
    ]);
    /* guests ride the day's first joined studio event (the prototype's guest
     * list is global). Second-phase fetch: needs the matched studio id. */
    let guests: [string, string, string][] = [];
    try {
      const rows0 = ((studioEvents as { events?: unknown[]; data?: unknown[] } | null)?.events || (studioEvents as { data?: unknown[] } | null)?.data || []) as Record<string, unknown>[];
      const sameDay0 = rows0.find((r) => {
        const iso = (r.startsAt || r.starts_at || r.start) as string | undefined;
        if (!iso) return false;
        const d = new Date(iso);
        return !isNaN(d.getTime()) && (d.toISOString().slice(0, 10) === dateISO || d.toLocaleDateString("en-CA") === dateISO);
      });
      if (sameDay0 && sameDay0.id) {
        const g = await listEventGuests(String(sameDay0.id), { limit: 30 });
        sources.push({ op: "listEventGuests", ok: true });
        guests = (((g as { data?: unknown[] }).data || []) as Record<string, unknown>[]).map((r) => [
          String(r.name || r.customer_name || r.guestName || ""),
          String(r.type || r.tier || r.notes || r.email || ""),
          String(r.status || "confirmed").replace("checked_in", "checked-in"),
        ] as [string, string, string]).filter((r) => r[0]);
      }
    } catch (eG) { sources.push({ op: "listEventGuests", ok: false, error: eG instanceof Error ? eG.message : String(eG) }); }
    if (my !== seq.current) return; /* a newer load superseded this one */
    if (!appts && !board) {
      setError(sources.map((s) => (!s.ok ? `${s.op}: ${s.error || "failed"}` : null)).filter(Boolean).join(" · ") || "No data");
      setLoading(false);
      setDay(null);
      return;
    }
    const b: OpsBoardSnapshot | null = board;
    /* Staff columns: the ops-board floor roster is who is actually on today.
     * Appointments referencing staff missing from it still get a column, so a
     * served booking can never silently vanish off the board. */
    const staff: FloorStaffCol[] = (b?.floor || []).map((f) => ({ staffId: f.staffId, name: f.name, role: f.role, status: f.status }));
    (appts?.appointments || []).forEach((a) => {
      const hit = staff.some((s) => (a.staffId && s.staffId === a.staffId) || (a.staffName && staffKey(s.name) === staffKey(a.staffName)));
      if (!hit && (a.staffId || a.staffName))
        staff.push({ staffId: a.staffId || "name:" + staffKey(a.staffName), name: a.staffName || a.staffId || "", role: "", status: null });
    });
    const blockRows: FloorBlockRow[] = ((blocks as any)?.blocks || (blocks as any)?.entries || []).map((x: any) => ({
      id: String(x.id), label: x.label || x.title || "Blocked", staffId: x.staffId ?? null,
      fromMin: anyToMin(x.start || x.from || ""), toMin: anyToMin(x.end || x.to || ""), kind: x.kind ?? null,
    }));
    setDay({
      dateISO,
      tz: b?.tz ?? null,
      locationId: b?.locationId ?? appts?.locationId ?? null,
      locationName: b?.locationName ?? null,
      staff,
      appointments: (appts?.appointments || []).map(apptRow),
      events: (b?.events || []).map((e) => {
        /* Title join + DATE CHECK: the events route ignores from/to, and the wf
         * layer's synthetic event day can disagree with the events family's
         * $rel date (seed-alignment note in the changelog). A CTA only exists
         * when Event Day would actually show the event TODAY — startsAt is
         * compared as a calendar day in the event's own zone via its raw ISO. */
        const sameDay = (r: any): boolean => {
          const iso = r?.startsAt || r?.starts_at || r?.start || null;
          if (!iso) return false;
          const d = new Date(iso);
          if (isNaN(d.getTime())) return false;
          return d.toISOString().slice(0, 10) === dateISO || d.toLocaleDateString("en-CA") === dateISO;
        };
        const rows = ((studioEvents as any)?.events || (studioEvents as any)?.data || []);
        const raw = rows.find((r: any) => {
          try { const u = toUIEvent(r); return u && staffKey(u.title) === staffKey(e.title) && sameDay(r); } catch { return false; }
        });
        const ui = raw ? toUIEvent(raw) : null;
        return {
          id: e.id, studioId: ui ? String(ui.id) : null, studio: raw || null,
          title: e.title, fromMin: anyToMin(e.start), toMin: anyToMin(e.end),
          registered: e.registered, checkedIn: e.checkedIn, staffOnShift: e.staffOnShift,
          staffNeeded: e.staffNeeded, status: e.status,
          resource: (e as unknown as { resource?: string | null }).resource ?? null,
        };
      }),
      queues: (b?.queues || []).map((q) => {
        const cq = (Array.isArray((consoleQueues as any)?.data) ? (consoleQueues as any).data : [])
          .find((x: any) => staffKey(x.name) === staffKey(q.name));
        return {
          id: q.id, consoleId: cq ? String(cq.id) : null,
          name: q.name, waiting: q.waiting, avgWaitMins: q.avgWaitMins,
          longestWaitMins: q.longestWaitMins, staffed: q.staffed, needed: q.needed, entries: q.entries || [],
        };
      }),
      blocks: blockRows,
      duty: (((dutyRes as any)?.duty || []) as any[]).map((r) => ({
        id: String(r.id), staffName: String(r.staff || ""), queueName: String(r.queue || ""),
        fromMin: hmToMin(String(r.from || "")), toMin: hmToMin(String(r.to || "")),
      })),
      /* one coverage block per role ASSIGNEE, spanning the role's window */
      coverage: (((staffingRes as any)?.items || []) as any[]).flatMap((ev) =>
        ((ev.roles || []) as any[]).flatMap((role) =>
          ((role.assignees || []) as any[]).map((a) => ({
            id: `${ev.eventId}:${role.id}:${a.staffId}`,
            eventId: String(ev.eventId), roleId: String(role.id), roleLabel: String(role.label || ""),
            eventTitle: String(ev.title || ""), staffId: String(a.staffId), staffName: String(a.staffName || ""),
            fromMin: anyToMin(String(role.window?.start || ev.start || "")),
            toMin: anyToMin(String(role.window?.end || ev.end || "")),
          })))),
      rooms: (((roomsRes as any)?.resources || (roomsRes as any)?.data || (roomsRes as any)?.items || []) as any[])
        .map((r) => ({ id: String(r.id), name: String(r.name || r.label || r.id) })),
      shifts: (((weekRes as any)?.shifts || []) as WfShift[])
        .filter((sh) => sh.date === dateISO && (sh.staffName || sh.staffId))
        .map((sh) => ({
          staffId: sh.staffId, staffName: sh.staffName || "",
          fromMin: hmToMin(sh.start), toMin: hmToMin(sh.end),
          breaks: (sh.breaks || []).map((br) => ({ fromMin: hmToMin(br.start), toMin: hmToMin(br.end), kind: br.kind })),
        })),
      prices: (((svcRes as any)?.services || (svcRes as any)?.data || []) as any[]).reduce((acc: FloorPriceMap, r: any) => {
        const amt = r?.price?.amount;
        if (typeof amt === "number" && r.name) acc[String(r.name).toLowerCase()] = amt;
        return acc;
      }, {} as FloorPriceMap),
      /* Outlook/Google busy: FIXED time — never draggable, pinned by the external calendar. */
      guests,
      threads: (((inboxRes as any)?.messages || (inboxRes as any)?.data || (inboxRes as any)?.items || []) as any[]).slice(0, 12).map((m: any, i2: number) => ({
        id: String(m.id || "in" + i2),
        name: String(m.customer_name || m.from || m.name || m.sender || "Customer"),
        chan: String(m.channel || "sms").toLowerCase(),
        when: String(m.received_at || m.when || ""),
        unread: !(m.read || m.is_read),
        preview: String(m.preview || m.subject || m.body || "").slice(0, 90),
        context: String(m.context || m.subject || ""),
        msgs: [],
      })),
      externalBusy: (((busyRes as any)?.blocks || []) as any[]).map((b) => ({
        id: String(b.id), label: String(b.label || "Busy") + " \u00b7 " + String(b.source || "external"),
        staffId: null, staffName: String(b.staff || ""),
        fromMin: hmToMin(String(b.from || "")), toMin: hmToMin(String(b.to || "")), kind: "external",
      })) as (FloorBlockRow & { staffName?: string })[],
      sources,
    });
    setLoading(false);
  }, [dateISO, locationId]);

  useEffect(() => { void load(); }, [load]);
  return { day, loading, error, reload: load };
}

/* ── writes (real ops; callers await and reload) ── */
export const moveAppointment = (a: Appointment, toMin: number, staffId: string | null, staffName: string | null) =>
  rescheduleAppointment(a.id, {
    date: a.date,
    start: minToHm(toMin),
    end: minToHm(toMin + (hmToMin(a.end) - hmToMin(a.start))),
    staffId, staffName,
    reason: "Moved on the Floor Calendar",
  });
/** Stage A (2026-08-08, PM-directed exact-UX port): the prototype board consumes
 * its OWN tenant shape — this maps the served day into that exact contract.
 * Statuses map served→prototype (booked/confirmed→ok, checked-in→now,
 * served→done, no-show→noshow); staff are indexed by roster position. */
export function toDCData(day: FloorDay, dayOffset = 0): Record<string, unknown> {
  const staffNames = day.staff.map((st) => st.name);
  const idx = (name: string | null | undefined, id?: string | null): number => {
    if (id != null) { const i = day.staff.findIndex((st) => st.staffId === id); if (i > -1) return i; }
    if (name) { const i = day.staff.findIndex((st) => staffKey(st.name) === staffKey(name)); if (i > -1) return i; }
    return -1;
  };
  const ST: Record<string, string> = { booked: "ok", confirmed: "ok", scheduled: "ok", "checked-in": "now", served: "done", "no-show": "noshow", cancelled: "cancelled" };
  const CH: Record<string, string> = { inPerson: "instore", in_person: "instore", phone: "phone", video: "video", onsite: "onsite" };
  /* stage C wave 1: the board's seeded entry ids are "ap" + index into this
   * array, so the ids ride ALONGSIDE the rows (same filters, same order) and the
   * host resolves "ap{n}" back to the served appointment for real writes. The
   * board's T() copies only its known keys, so bookIds is host-side metadata. */
  const bookRows = day.appointments
    .filter((a) => a.appt.status !== "cancelled")
    .map((a) => ({ id: a.appt.id, appt: a.appt, row: [idx(a.staffName, a.staffId), a.fromMin, a.toMin - a.fromMin, a.appt.customer.name, a.appt.service, ST[a.appt.status] || "ok", CH[a.appt.channel] || "instore"] }))
    .filter((r) => (r.row[0] as number) > -1);
  const book = bookRows.map((r) => r.row);
  /* stage C wave 2: like bookIds — the board's duty ids are "qd" + index into the
   * pushed rows, so the served ids ride alongside in the same order. */
  const dutyRows = day.duty.map((d) => {
    const q = day.queues.find((x) => staffKey(x.name) === staffKey(d.queueName));
    return { id: d.id ?? null, row: [idx(d.staffName), d.fromMin, d.toMin, q ? q.id : d.queueName] };
  }).filter((r) => (r.row[0] as number) > -1);
  const queueDuty = dutyRows.map((r) => r.row);
  const external = day.externalBusy.map((b) => [idx(b.staffName || null), b.fromMin, b.toMin - b.fromMin, b.label]).filter((r) => (r[0] as number) > -1);
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const queue = day.queues.flatMap((q) =>
    q.entries.map((e) => {
      const joined = new Date(e.joinedAt);
      const jm = isNaN(joined.getTime()) ? nowMin - 5 : joined.getHours() * 60 + joined.getMinutes();
      return [0, jm, Math.max(1, nowMin - jm), e.name, e.service || q.name, "SMS", e.status === "serving" ? "serving" : e.status === "called" ? "called" : "waiting", q.id, false];
    }));
  /* stage B: shifts [staffIdx, from, to, breakFrom, breakTo] — a shiftless
   * roster member gets the board's full window with a zero-length break */
  const shifts = day.staff.map((st, i) => {
    const sh = day.shifts.find((x) => (x.staffId && x.staffId === st.staffId) || staffKey(x.staffName) === staffKey(st.name));
    if (!sh) return [i, DAY_START + 60, DAY_END - 60, DAY_START + 60, DAY_START + 60];
    const br = sh.breaks[0];
    return [i, sh.fromMin, sh.toMin, br ? br.fromMin : sh.fromMin, br ? br.toMin : sh.fromMin];
  });
  /* stage B: events in the prototype's shape. `eventsOn(off)` filters day === off,
   * so they are keyed to the day the board is SHOWING — the host re-reads the
   * served families when the board changes day (floor-day seam, 2026-08-10). */
  /* One event per id: the served families can describe the same event twice (an
   * ops-board row and its studio twin), and the board draws one block per row. */
  const seenEvent = new Set<string>();
  const events = day.events.filter((e) => {
    const k = staffKey(e.title) + ":" + e.fromMin;
    if (seenEvent.has(k)) return false;
    seenEvent.add(k);
    return true;
  }).map((e) => {
    const cover = day.coverage.filter((c) => c.eventId === e.id || staffKey(c.eventTitle) === staffKey(e.title));
    const st = (e.studio || {}) as Record<string, unknown>;
    /* One block per PERSON, not per assignment: coverage lists a row per role, so
     * anyone covering two roles on the same event used to render it twice in their
     * own column (operator-reported, 2026-08-10). */
    const staffIdx = [...new Set(cover.map((c) => idx(c.staffName, c.staffId)).filter((n) => n > -1))];
    return {
      id: e.studioId || e.id, day: dayOffset, title: e.title, from: e.fromMin, to: e.toMin,
      staff: staffIdx,
      /* the event's OWN room when the served row names one (REQ-CAL-4 asks for
       * this field on the contract; the ops-board family carries it today) */
      room: e.resource || day.rooms[2]?.name || day.rooms[0]?.name || "",
      cap: typeof st.capacity === "number" ? st.capacity : e.registered,
      reg: e.registered, waitlist: typeof st.waitlist_count === "number" ? st.waitlist_count : 0,
      owner: cover[0]?.staffName || String(st.staffName || ""),
      cat: "event", desc: String(st.description || ""),
    };
  });
  return {
    staff: day.staff.map((st) => [st.name, st.role || ""]),
    bookIds: bookRows.map((r) => r.id),
    dutyIds: dutyRows.map((r) => r.id),
    book, queueDuty, external, queue, shifts, events,
    guests: day.guests,
    threads: day.threads,
    rooms: day.rooms.map((r) => r.name),
    queues: day.queues.map((q) => ({ id: q.id, name: q.name, resource: null, sla: 15, skills: [] })),
    location: day.locationName || undefined,
    tz: day.tz || undefined,
  };
}

/** Duty drag commit — the engine's move ledger makes this a REAL served write
 *  in the design environment (REQ-CAL-3 tracks the production op). */
export const moveQueueDuty = (id: string, patch: { staff?: string; from?: string; to?: string }) =>
  apiRaw(`/v1/admin/floor/queue-duty/${encodeURIComponent(id)}/move`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(patch) }, "moveQueueDuty");
export { assignEventRole };
export { cancelAppointment, checkInAppointment, markAppointmentNoShow, createCalendarDayBlock, deleteCalendarDayBlock };
